import { useEffect, useMemo, useState } from "react";
import {
    CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Icon, InputAdornment, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { TEXTO_COLORIDO_ESCURO, coresDoTema } from "assets/coresTema";
import { get } from "utils/WebService";
import { errorAlert } from "utils/Functions";
import { formatarNumero, passaNoFiltro } from "./analiseUtils";
import { formatarMoedaCentavos } from "./acordosUtils";
import { baixarCsv, decimalCsv } from "./csv";

// Colunas de cada tipo de caso (nomes como vêm do backend, AnaliseTempoService.obterCasos)
const PESSOAS = [
    { id: "Negociador", rotulo: "Negociador" },
    { id: "Coordenador", rotulo: "Coordenador" },
];
const COLUNAS = {
    fichas: [
        { id: "Ficha", rotulo: "Ficha", formato: "codigo" },
        { id: "Devedor", rotulo: "Devedor" },
        { id: "CodigoCliente", rotulo: "Código cliente", formato: "codigo" },
        { id: "Cliente", rotulo: "Cliente" },
        { id: "Carteira", rotulo: "Carteira" },
        { id: "StatusFicha", rotulo: "Status de hoje" },
        ...PESSOAS,
        { id: "Atendimentos", rotulo: "Atendimentos", formato: "numero" },
        { id: "MesesAcionada", rotulo: "Meses acionada", formato: "numero" },
        { id: "UltimoAcionamento", rotulo: "Último acionamento", formato: "data" },
    ],
    acordos: [
        { id: "NumeroAcordo", rotulo: "Acordo", formato: "codigo" },
        { id: "Formalizacao", rotulo: "Formalização", formato: "data" },
        { id: "Ficha", rotulo: "Ficha", formato: "codigo" },
        { id: "Devedor", rotulo: "Devedor" },
        { id: "CodigoCliente", rotulo: "Código cliente", formato: "codigo" },
        { id: "Cliente", rotulo: "Cliente" },
        ...PESSOAS,
        { id: "ValorOriginal", rotulo: "Valor original", formato: "moeda" },
        { id: "Desconto", rotulo: "Desconto", formato: "moeda" },
        { id: "ValorAcordo", rotulo: "Valor do acordo", formato: "moeda" },
        { id: "Parcelas", rotulo: "Parcelas", formato: "numero" },
        { id: "Situacao", rotulo: "Situação hoje" },
        { id: "Cancelamento", rotulo: "Cancelamento", formato: "data" },
        { id: "PagoAteHoje", rotulo: "Pago até hoje", formato: "moeda" },
        { id: "ContaNaConversao", rotulo: "Conta na conversão" },
    ],
    pagamentos: [
        { id: "DataPagamento", rotulo: "Pagamento", formato: "data" },
        { id: "ValorPago", rotulo: "Valor pago", formato: "moeda" },
        { id: "Origem", rotulo: "Origem" },
        { id: "NumeroAcordo", rotulo: "Acordo", formato: "codigo" },
        { id: "Parcela", rotulo: "Parcela", formato: "numero" },
        { id: "VencimentoParcela", rotulo: "Vencimento", formato: "data" },
        { id: "Contrato", rotulo: "Contrato", formato: "codigo" },
        { id: "Ficha", rotulo: "Ficha", formato: "codigo" },
        { id: "Devedor", rotulo: "Devedor" },
        { id: "CodigoCliente", rotulo: "Código cliente", formato: "codigo" },
        { id: "Cliente", rotulo: "Cliente" },
        ...PESSOAS,
        { id: "DataBaixa", rotulo: "Baixa", formato: "data" },
    ],
};
const ORDEM_INICIAL = {
    fichas: { coluna: "Atendimentos", direcao: "desc" },
    acordos: { coluna: "Formalizacao", direcao: "asc" },
    pagamentos: { coluna: "DataPagamento", direcao: "asc" },
};
const LIMITE_NA_TELA = 1000;

/**
 * Abre a ficha numa aba nova do navegador, só para consulta (FichaAvulsa). A aba nova recebe o login e o tema desta pelo
 * mesmo esquema da Monitoria: o App.js lê e apaga os temp_share_* ao abrir.
 */
const abrirFicha = (linha) => {
    [["temp_share_user", "user"], ["temp_share_menus", "userMenus"], ["temp_share_tema", "tema"]].forEach(([destino, origem]) => {
        const valor = sessionStorage.getItem(origem);
        if (valor) localStorage.setItem(destino, valor);
    });
    window.open(`/system?view=ficha&devedor=${encodeURIComponent(linha.idDevedor)}&ficha=${encodeURIComponent(linha.Ficha)}`, "_blank");
};

const formatarData = (iso) => {
    if (!iso) return "";
    const [ano, mes, dia] = String(iso).slice(0, 10).split("-");
    return dia ? `${dia}/${mes}/${ano}` : String(iso);
};

const formatar = (formato, valor) => {
    if (valor === null || valor === undefined || valor === "") return "–";
    if (formato === "moeda") return formatarMoedaCentavos(Number(valor));
    if (formato === "numero") return formatarNumero(Number(valor));
    if (formato === "data") return formatarData(valor);
    return String(valor);
};

const comparar = (formato, a, b) => {
    if (a === null || a === undefined || a === "") return 1;
    if (b === null || b === undefined || b === "") return -1;
    if (formato === "moeda" || formato === "numero") return Number(a) - Number(b);
    return String(a).localeCompare(String(b), "pt-BR", { numeric: true });
};

/**
 * "Ver casos" de um card da Análise de Atendimento: os casos que formam o número, com as mesmas regras e filtros da tela
 * (período, carteira e status no backend; cliente e pessoa aqui). tipo: fichas (card Fichas Acionadas, que já traz os
 * atendimentos de cada ficha) | acordos (card Negociações, que já traz o valor e se conta na conversão) | pagamentos (card
 * Valor Recebido). O resumo do topo bate com os cards.
 */
export default function CasosDialog({
    tipo, titulo, parametrosConsulta, clientes, pessoas, campoPessoa, geradoEmTela, onFechar,
}) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = coresDoTema(darkMode);
    const [carregando, setCarregando] = useState(true);
    const [resposta, setResposta] = useState(null);
    const [busca, setBusca] = useState("");
    const [ordem, setOrdem] = useState(ORDEM_INICIAL[tipo]);

    useEffect(() => {
        let vigente = true;
        const url = `/relatorios/analise-tempo/casos?tipo=${tipo}&${parametrosConsulta}`;
        setCarregando(true);
        get(url)
            // Lista guardada antes do Atualizar da tela: busca de novo, para bater com os números
            .then(res => (geradoEmTela && res?.geradoEm && res.geradoEm < geradoEmTela ? get(`${url}&atualizar=true`) : res))
            .then(res => { if (vigente) setResposta(res); })
            .catch(err => { if (vigente) errorAlert(err?.message || "Erro ao carregar os casos."); })
            .finally(() => { if (vigente) setCarregando(false); });
        return () => { vigente = false; };
    }, [tipo, parametrosConsulta, geradoEmTela]);

    const colunaPessoa = campoPessoa === "coordenador" ? "Coordenador" : "Negociador";
    const colunas = COLUNAS[tipo];

    // Mesmos filtros da tela: cliente e pessoa (da aba)
    const doEscopo = useMemo(() => (resposta?.linhas || [])
        .filter(l => passaNoFiltro(clientes, l.Cliente))
        .filter(l => passaNoFiltro(pessoas, l[colunaPessoa])), [resposta, clientes, pessoas, colunaPessoa]);

    const visiveis = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const coluna = colunas.find(c => c.id === ordem.coluna) || colunas[0];
        const sinal = ordem.direcao === "asc" ? 1 : -1;
        return doEscopo
            .filter(l => !termo || colunas.some(c => String(l[c.id] ?? "").toLowerCase().includes(termo) || formatar(c.formato, l[c.id]).toLowerCase().includes(termo)))
            .sort((a, b) => sinal * comparar(coluna.formato, a[coluna.id], b[coluna.id]));
    }, [doEscopo, busca, ordem, colunas]);

    // Resumo que bate com o card (sobre o escopo, sem a busca)
    const somar = (campo) => doEscopo.reduce((acc, l) => acc + (Number(l[campo]) || 0), 0);
    let resumo = "";
    if (tipo === "fichas") {
        // Sem pessoa escolhida, o card conta cada ficha uma vez; com pessoas, soma as fichas de cada uma
        const fichas = new Set(doEscopo.map(l => (pessoas.length ? `${l[colunaPessoa]}|${l.Ficha}` : l.Ficha))).size;
        resumo = `${formatarNumero(fichas)} fichas acionadas · ${formatarNumero(somar("Atendimentos"))} atendimentos`;
        if (doEscopo.length > fichas) {
            resumo += ` (${formatarNumero(doEscopo.length)} linhas: a ficha acionada por mais de uma pessoa aparece uma vez para cada)`;
        }
    } else if (tipo === "acordos") {
        const naConversao = doEscopo.filter(l => l.ContaNaConversao === "Sim").length;
        resumo = `${formatarNumero(doEscopo.length)} acordos · ${formatarMoedaCentavos(somar("ValorAcordo"))} negociados · `
            + `${formatarNumero(naConversao)} contam na conversão`;
    } else {
        resumo = `${formatarNumero(doEscopo.length)} pagamentos · ${formatarMoedaCentavos(somar("ValorPago"))} recebidos`;
    }

    const baixar = () => baixarCsv(`analise_atendimento_${tipo}.csv`, [
        colunas.map(c => c.rotulo),
        ...visiveis.map(l => colunas.map(c => {
            if (c.formato === "moeda") return decimalCsv(l[c.id]);
            if (c.formato === "data") return formatarData(l[c.id]);
            return l[c.id] ?? "";
        })),
    ]);

    const celula = {
        display: "table-cell", py: 0.6, px: 1, fontSize: "0.78rem", color: `${tema.texto} !important`,
        borderBottom: `1px solid ${tema.divisoria}`, verticalAlign: "middle", whiteSpace: "nowrap",
    };
    const cabecalho = { ...celula, fontWeight: "bold", color: `${tema.textoSecundario} !important`, fontSize: "0.72rem", background: `${tema.painel} !important` };
    const alinhar = (c) => (c.formato === "moeda" || c.formato === "numero" ? "right" : "left");
    const corLink = darkMode ? TEXTO_COLORIDO_ESCURO.info : "#1A73E8";
    const conteudo = (c, l) => {
        if (c.id !== "Ficha" || !l.idDevedor || !l.Ficha) return formatar(c.formato, l[c.id]);
        return (
            <Tooltip title="Abrir a ficha em outra aba" placement="right">
                <MDBox
                    component="button"
                    type="button"
                    onClick={() => abrirFicha(l)}
                    display="inline-flex"
                    alignItems="center"
                    gap={0.4}
                    sx={{
                        border: 0, background: "transparent", p: 0, cursor: "pointer", font: "inherit",
                        color: `${corLink} !important`, textDecoration: "underline", fontWeight: 600,
                    }}
                >
                    {l.Ficha}
                    <Icon sx={{ fontSize: "0.85rem !important", color: "inherit" }}>open_in_new</Icon>
                </MDBox>
            </Tooltip>
        );
    };
    const ordenar = (id) => setOrdem(atual => ({ coluna: id, direcao: atual.coluna === id && atual.direcao === "asc" ? "desc" : "asc" }));

    return (
        <Dialog open onClose={onFechar} maxWidth="xl" fullWidth PaperProps={{ sx: { background: tema.cartao, color: tema.texto } }}>
            <DialogTitle sx={{ pb: 0.5, color: `${tema.texto} !important` }}>{titulo}</DialogTitle>
            <DialogContent sx={{ pt: "4px !important" }}>
                {carregando ? (
                    <MDBox display="flex" justifyContent="center" py={6}><CircularProgress size={30} color="info" /></MDBox>
                ) : (
                    <>
                        <MDTypography variant="button" fontWeight="bold" color="text" sx={{ display: "block" }}>{resumo}</MDTypography>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5, opacity: 0.85 }}>
                            Mesmas regras e filtros da tela (período, carteira, status, cliente e {colunaPessoa.toLowerCase()}).
                            {resposta?.acimaDoLimite && " A lista passou do limite e veio cortada: use mais filtros ou a planilha (Exportar)."}
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={1.5}>
                            <MDBox sx={{ width: 280 }}>
                                <MDInput
                                    size="small"
                                    fullWidth
                                    placeholder="Buscar devedor, ficha, código, pessoa..."
                                    value={busca}
                                    onChange={e => setBusca(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon fontSize="small" sx={{ color: `${tema.textoSecundario} !important` }}>search</Icon>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </MDBox>
                            {busca && (
                                <MDTypography variant="caption" color="text">
                                    {formatarNumero(visiveis.length)} de {formatarNumero(doEscopo.length)} linhas
                                </MDTypography>
                            )}
                            <MDBox flex={1} />
                            <MDButton variant="outlined" color="info" size="small" onClick={baixar} disabled={!visiveis.length}>
                                <Icon sx={{ mr: 0.5 }}>download</Icon> Baixar CSV
                            </MDButton>
                        </MDBox>
                        <TableContainer sx={{ boxShadow: "none", background: "transparent", borderRadius: 0, maxHeight: "60vh" }}>
                            <Table size="small" stickyHeader sx={{ display: "table" }}>
                                <TableHead sx={{ display: "table-header-group" }}>
                                    <TableRow sx={{ display: "table-row" }}>
                                        {colunas.map(c => (
                                            <TableCell key={c.id} align={alinhar(c)} sx={cabecalho}>
                                                <TableSortLabel
                                                    active={ordem.coluna === c.id}
                                                    direction={ordem.coluna === c.id ? ordem.direcao : "asc"}
                                                    onClick={() => ordenar(c.id)}
                                                    sx={{ color: "inherit !important", "& .MuiTableSortLabel-icon": { color: "inherit !important" } }}
                                                >
                                                    {c.rotulo}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ display: "table-row-group" }}>
                                    {visiveis.slice(0, LIMITE_NA_TELA).map((l, i) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <TableRow key={i} sx={{ display: "table-row", "&:hover": { background: tema.hover } }}>
                                            {colunas.map(c => (
                                                <TableCell key={c.id} align={alinhar(c)} sx={celula}>{conteudo(c, l)}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                    {visiveis.length === 0 && (
                                        <TableRow sx={{ display: "table-row" }}>
                                            <TableCell colSpan={colunas.length} align="center" sx={celula}>Nenhum caso.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {visiveis.length > LIMITE_NA_TELA && (
                            <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                                Mostrando {formatarNumero(LIMITE_NA_TELA)} de {formatarNumero(visiveis.length)} linhas: use a busca ou baixe o CSV, que leva todas.
                            </MDTypography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <MDButton variant="outlined" color="info" onClick={onFechar}>Fechar</MDButton>
            </DialogActions>
        </Dialog>
    );
}
